// Банерите над листа на документа „Доставка": анулирана · стокова във фактура
// (синьо/инфо — „Отвори фактурата" и „Извади от фактурата") · приключена без
// фактура („Върни за фактуриране") · фактура, обобщила N стокови (значки към
// тях). Действията идват само когато документът е отворен от ред.
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { Delivery } from "../../../../api/inventory";
import { SECONDARY_BUTTON_CLASS } from "../../components/list/buttonStyles";
import { isDeliveryAnnulled, noteInvoiceStateOf } from "./deliveryMeta";

type Props = {
  delivery: Delivery;
  marginClass: string;
  onOpenDelivery?: (id: number) => void;
  onDetach?: () => void;
  onReopen?: () => void;
};

const BANNER_CLASS = "rounded-[9px] px-3.5 py-2.5 text-[13.5px] leading-[1.45]";
const BANNER_BUTTON_CLASS = `${SECONDARY_BUTTON_CLASS} !py-1.5`;

export const DeliveryDocumentBanners = ({ delivery, marginClass, onOpenDelivery, onDetach, onReopen }: Props) => {
  const { t } = useTranslation();
  const annulled = isDeliveryAnnulled(delivery);
  const noteState = noteInvoiceStateOf(delivery);
  const linkedNotes = delivery.linkedNoteIds ?? [];
  return (
    <>
      {annulled && (
        <div className={`${BANNER_CLASS} ${marginClass} bg-fill-2 text-ink-2`}>
          {t("inventoryPage.annulledBanner", {
            date: delivery.deletedAt ? dayjs(delivery.deletedAt).format("DD.MM.YYYY HH:mm") : "—",
          })}
          {delivery.deletedByName ? ` · ${delivery.deletedByName}` : ""}
          {delivery.deleteReason ? ` · ${delivery.deleteReason}` : ""}
        </div>
      )}
      {noteState === "inInvoice" && (
        <div
          className={`${BANNER_CLASS} ${marginClass} flex flex-wrap items-center gap-2.5 bg-info-tint text-info-deep`}
        >
          <span className="flex-1">{t("inventoryPage.noteInInvoiceBanner")}</span>
          {onOpenDelivery && (
            <button
              type="button"
              onClick={() => onOpenDelivery(delivery.invoiceId as number)}
              className={BANNER_BUTTON_CLASS}
            >
              {t("inventoryPage.openInvoice")}
            </button>
          )}
          {!annulled && onDetach && (
            <button type="button" onClick={onDetach} className={BANNER_BUTTON_CLASS}>
              {t("inventoryPage.noteDetach")}
            </button>
          )}
        </div>
      )}
      {noteState === "closed" && (
        <div className={`${BANNER_CLASS} ${marginClass} flex flex-wrap items-center gap-2.5 bg-fill text-ink-2`}>
          <span className="min-w-0 flex-1">
            {t("inventoryPage.noteClosedBanner", {
              date: dayjs(delivery.closedWithoutInvoiceAt).format("DD.MM.YYYY"),
            })}
            {delivery.closedWithoutInvoiceByName ? ` · ${delivery.closedWithoutInvoiceByName}` : ""}
            {delivery.closedWithoutInvoiceReason ? ` · ${delivery.closedWithoutInvoiceReason}` : ""}
          </span>
          {!annulled && onReopen && (
            <button type="button" onClick={onReopen} className={BANNER_BUTTON_CLASS}>
              {t("inventoryPage.noteReopen")}
            </button>
          )}
        </div>
      )}
      {linkedNotes.length > 0 && (
        <div className={`${BANNER_CLASS} ${marginClass} flex flex-wrap items-center gap-2 bg-info-tint text-info-deep`}>
          <span>{t("inventoryPage.invoiceLinksNotes", { count: linkedNotes.length })}</span>
          {onOpenDelivery &&
            linkedNotes.map((noteId) => (
              <button
                key={noteId}
                type="button"
                onClick={() => onOpenDelivery(noteId)}
                className="cursor-pointer rounded-md border border-info/30 bg-surface px-2 py-0.5 text-xs text-ink hover:border-info"
              >
                {t("inventoryPage.internalNo", { id: noteId })}
              </button>
            ))}
        </div>
      )}
    </>
  );
};

export default DeliveryDocumentBanners;
